import { AxiosInstance } from 'axios';
import { useQuery } from 'react-query';
import { axiosNoAuth } from './axios';
import { i18nAppend } from '../helpers/i18nAppend';
import { RecapTranslationsType } from '../types/RecapTranslationsType';
import type { RecapDisplayCardType } from '../types/Cards/RecapDisplayCardType';
import type { RecapLegacyCardType } from '../types/Cards/RecapLegacyCardType';

const RECAP_QUERY_KEY = ['recap'] as const;

const filterRecapTranslations = (translations: RecapTranslationsType[]) => {
  return translations.filter((obj) => obj.id != null);
};

const getYearFromText = (...values: Array<string | number | null | undefined>) => {
  for (const value of values) {
    const year = value?.toString().match(/\d{4}/)?.[0];
    if (year) return year;
  }
  return '';
};

const getLegacyCardLink = (card: RecapLegacyCardType) => card.legacy_card_link || card.link || '';

const getLegacyCardTitle = (card: RecapLegacyCardType) => card.legacy_card_title || card.title || '';

const getDisplayCards = (displayCards: RecapDisplayCardType[] | null | undefined) => {
  return Array.isArray(displayCards) ? displayCards : [];
};

const getLegacyMarketingReleaseLinksByYear = (legacyCards: RecapLegacyCardType[] = []) => {
  if (!Array.isArray(legacyCards)) return new Map<string, string>();

  return legacyCards.reduce((linksByYear, card) => {
    const link = getLegacyCardLink(card);
    const year = getYearFromText(getLegacyCardTitle(card), link);

    if (year && link && !linksByYear.has(year)) {
      linksByYear.set(year, link);
    }

    return linksByYear;
  }, new Map<string, string>());
};

const getDisplayCardMarketingReleaseLink = (card: RecapDisplayCardType, legacyLinksByYear: Map<string, string>) => {
  const year = getYearFromText(card.year, card.title, card.date);
  return year ? legacyLinksByYear.get(year) ?? '' : '';
};

const appendRecapTranslations = (recapTranslations: RecapTranslationsType[]) => {
  i18nAppend(recapTranslations, 'recap_banner', {
    cover_description: '',
    cover_image: '',
    cover_image_alt_text: '',
    cover_section_name: '',
    cover_title: '',
  });

  i18nAppend(recapTranslations, 'recap_highlights', {
    highlight_card: [{
      description: '',
      image: '',
      image_alt_text: '',
      title: '',
      section_title: '',
    }],
    highlight_description: '',
    highlight_read_more_link: '',
    highlight_read_more_text: '',
    highlight_section_name: '',
    highlight_title: '',
    highlight_video_link: '',
    highlight_video_thumbnail: '',
  });

  i18nAppend(recapTranslations, 'recap_legacy', {
    legacy_card: [{
      legacy_card_link: '',
      legacy_card_image: '',
      legacy_card_image_alt_text: '',
      legacy_card_title: '',
    }],
    legacy_section_name: '',
    legacy_title: '',
  });

  i18nAppend(recapTranslations, 'recap_display', {
    display_description: '',
    display_title: '',
    display_card: [{
      date: '',
      description: '',
      image: '',
      link: '',
      location: '',
      theme: '',
      title: '',
      year: '',
    }],
    legacy_section_name: '',
    legacy_title: '',
  });
};

export const getRecapTranslations = async (axios: AxiosInstance) => {
  const response = await axios.get<RecapTranslationsType[]>('/hackathon-cms/v1/marksite/recap');
  const filteredDisplayTranslations = filterRecapTranslations(response.data);

  if (filteredDisplayTranslations.length === 0) {
    appendRecapTranslations([]);
    return [];
  }

  const fallbackTranslations = filteredDisplayTranslations[0];
  const fallbackDisplayCards = getDisplayCards(fallbackTranslations.display_card);
  const fallbackLegacyLinksByYear = getLegacyMarketingReleaseLinksByYear(fallbackTranslations.legacy_card);
  const displayTranslationsWithLinks = filteredDisplayTranslations.map((translations) => {
    const legacyLinksByYear = new Map(fallbackLegacyLinksByYear);
    getLegacyMarketingReleaseLinksByYear(translations.legacy_card).forEach((link, year) => {
      legacyLinksByYear.set(year, link);
    });

    const translatedDisplayCards = getDisplayCards(translations.display_card);
    const displayCards = translatedDisplayCards.length > 0 ? translatedDisplayCards : fallbackDisplayCards;

    return {
      ...translations,
      display_description: translations.display_description || fallbackTranslations.display_description,
      display_title: translations.display_title || fallbackTranslations.display_title,
      display_card: displayCards.map((card) => ({
        ...card,
        link: getDisplayCardMarketingReleaseLink(card, legacyLinksByYear),
      })),
    };
  });

  appendRecapTranslations(displayTranslationsWithLinks);

  return displayTranslationsWithLinks;
};

export const useRecapTranslationsQuery = () => {
  return useQuery<RecapTranslationsType[]>({
    queryKey: RECAP_QUERY_KEY,
    queryFn: () => getRecapTranslations(axiosNoAuth),
    staleTime: Infinity,
    retry: 2,
    refetchOnWindowFocus: false,
  });
};

const getDefaultRecapTranslation = (recapTranslations: RecapTranslationsType[]) => {
  return recapTranslations[0];
};

export const getHistoryHighlightsTranslations = (recapTranslations: RecapTranslationsType[]) => {
  return (getDefaultRecapTranslation(recapTranslations)?.highlight_card ?? []).map((_, index) => index);
};

export const getHistoryLegacyTranslations = (recapTranslations: RecapTranslationsType[]) => {
  return (getDefaultRecapTranslation(recapTranslations)?.legacy_card ?? []).map((_, index) => index);
};

export const getHistoryDisplayTranslations = (recapTranslations: RecapTranslationsType[]) => {
  return (getDefaultRecapTranslation(recapTranslations)?.display_card ?? []).map((_, index) => index);
};